/**
 * Décor procédural déterministe : forêt de sapins, rochers, panneaux et
 * jalons de piste, petits chalets du village (voir village.ts). Rien ne transite sur le réseau : client et graine de carte suffisent.
 * Les instances sont rangées par tuile de terrain : une tuile invisible
 * (hors champ ou cachée par le relief) n'est même pas parcourue.
 */
import * as THREE from 'three/webgpu';
import {
  Rng,
  createNoise2D,
  distPointSegment,
  fbm,
  smoothstep,
  type Building,
  type GameMap,
  type Heightfield,
  type Lift,
  type Piste,
  type Vec2,
  buildingRadius,
} from '@snowchain/shared';
import { InstancedLodSet, composeYawRoll, linear, type Frustum } from './instancing';
import type { AssetLibrary } from './assets';
import type { PisteRaster } from './piste-raster';
import { chunksPerSide, type Terrain } from './terrain';
import { LOD_DISTANCES, farther, mapViewScale } from './lod';
import { placePistePoles, placeVillageHouses } from './village';

const ROCK_KINDS = ['rock_a', 'rock_b', 'rock_c'];

interface InstanceBatch {
  set: InstancedLodSet;
  /** Matrices (16 flottants par instance). */
  m: Float32Array;
  /** Centre et rayon de la sphère englobante (4 flottants par instance). */
  s: Float32Array;
  tint: Float32Array;
  /** Index de tuile par instance. */
  chunk: Uint16Array;
  count: number;
  /** Instances triées par tuile : début et fin de chaque tuile. */
  ranges: Uint32Array;
}

export const DIFFICULTY_TINT: Record<string, number> = { verte: 0x4e7a5a, bleue: 0x2e5d74, rouge: 0xd96e30, noire: 0x1f2d37 };

export class Decor {
  readonly group = new THREE.Group();
  private batches: InstanceBatch[] = [];
  private sets: Record<string, InstancedLodSet> = {};
  treeCount = 0;
  rockCount = 0;
  houseCount = 0;
  poleCount = 0;
  /** Tuiles de terrain par côté (rangement des instances). */
  private readonly side: number;
  private natural: { trees: Float64Array; rocks: Float64Array } | null = null;

  constructor(
    private readonly lib: AssetLibrary,
    private readonly hf: Heightfield,
    private readonly map: GameMap,
  ) {
    this.group.name = 'decor';
    this.side = chunksPerSide(hf);
    const mk = (name: string, cap: number, dist: number[], shadowLods: number) =>
      (this.sets[name] = new InstancedLodSet(name, this.group, lib.get(name).geometries, lib.instanced, cap, dist, { shadowLods }));
    const far = mapViewScale(hf.half);
    mk('tree_fir_a', 14_000, farther(LOD_DISTANCES.tree, far), 1);
    mk('tree_fir_b', 14_000, farther(LOD_DISTANCES.tree, far), 1);
    mk('rock_a', 1500, farther(LOD_DISTANCES.rock, far), 1);
    mk('rock_b', 1500, farther(LOD_DISTANCES.rock, far), 1);
    mk('rock_c', 1200, farther(LOD_DISTANCES.rock, far), 1);
    mk('sign_piste', 400, LOD_DISTANCES.prop, 1);
    mk('chalet_small_a', 64, LOD_DISTANCES.house, 1);
    mk('chalet_small_b', 64, LOD_DISTANCES.house, 1);
    mk('piste_pole', 1024, LOD_DISTANCES.marker, 0);
  }

  /** Régénère les placements (au démarrage et après chaque changement de domaine). */
  rebuild(raster: PisteRaster, liftsIn: Iterable<Lift>, buildingsIn: Iterable<Building>, pistesIn: Iterable<Piste>): void {
    // Les entrées peuvent être des itérateurs à usage unique : on les fige.
    const lifts = [...liftsIn];
    const buildings = [...buildingsIn];
    const pistes = [...pistesIn];
    const hf = this.hf;
    const map = this.map;
    const liftSegs = [...lifts].map((l) => [l.bottom, l.top] as [Vec2, Vec2]);
    const blds = [...buildings].map((b) => ({ x: b.x, z: b.z, r: buildingRadius(b.kind) + 5 }));
    const lake = map.features.lake;
    const lists: Record<string, { x: number; y: number; z: number; yaw: number; s: number; tint: [number, number, number] }[]> = {};
    const push = (name: string, x: number, y: number, z: number, yaw: number, s: number, tint: [number, number, number] = [1, 1, 1]) => {
      (lists[name] ??= []).push({ x, y, z, yaw, s, tint });
    };
    const blocked = (x: number, z: number, margin: number): boolean => {
      if (raster.sample(x, z).cov > 0.01) return true;
      for (const [a, b] of liftSegs) if (distPointSegment([x, z], a, b) < 13 + margin) return true;
      for (const b of blds) if (Math.hypot(x - b.x, z - b.z) < b.r + margin) return true;
      if (Math.hypot(x - lake.x, z - lake.z) < lake.radius + 6) return true;
      return false;
    };
    // Petits chalets du village : hors pistes avec 10 m de marge (raster), orientés vers l'aval.
    const onPiste = (x: number, z: number): boolean => {
      if (raster.sample(x, z).cov > 0.01) return true;
      for (let k = 0; k < 8; k++) {
        const a = (k * Math.PI) / 4;
        if (raster.sample(x + Math.cos(a) * 10, z + Math.sin(a) * 10).cov > 0.01) return true;
      }
      return false;
    };
    const houses = placeVillageHouses(hf, map, onPiste, lifts, buildings);
    for (const h of houses) {
      push(h.name, h.x, h.y, h.z, h.yaw, h.s);
      // Ni sapin ni rocher dans un jardin.
      blds.push({ x: h.x, z: h.z, r: 8 * h.s });
    }
    // Forêt et rochers : tirages et critères du relief nu faits une fois par carte ;
    // à chaque reconstruction, seuls les abords des pistes, remontées et bâtiments sont écartés.
    const nat = this.naturalDecor();
    const trees = nat.trees;
    for (let k = 0; k < trees.length; k += 5) {
      const x = trees[k] as number;
      const z = trees[k + 1] as number;
      // Lisière dégagée autour des pistes (2 m de plus que l'emprise).
      if (blocked(x, z, 0) || raster.sample(x + 4, z).cov > 0.01 || raster.sample(x - 4, z).cov > 0.01 || raster.sample(x, z + 4).cov > 0.01 || raster.sample(x, z - 4).cov > 0.01) continue;
      push(trees[k + 4] ? 'tree_fir_b' : 'tree_fir_a', x, hf.height(x, z) - 0.3, z, trees[k + 2] as number, trees[k + 3] as number);
    }
    const rocks = nat.rocks;
    for (let k = 0; k < rocks.length; k += 5) {
      const x = rocks[k] as number;
      const z = rocks[k + 1] as number;
      const sc = rocks[k + 3] as number;
      if (blocked(x, z, 2)) continue;
      push(ROCK_KINDS[rocks[k + 4] as number] as string, x, hf.height(x, z) - 0.25 * sc, z, rocks[k + 2] as number, sc);
    }
    // Panneaux de piste : au départ puis tous les 240 m, en bordure.
    for (const p of pistes) {
      const tint = linear(DIFFICULTY_TINT[p.difficulty] ?? 0xffffff);
      let acc = 0;
      let next = 10;
      for (let i = 0; i < p.path.length - 1; i++) {
        const a = p.path[i] as Vec2;
        const b = p.path[i + 1] as Vec2;
        const seg = Math.hypot(b[0] - a[0], b[1] - a[1]);
        while (next <= acc + seg) {
          const t = (next - acc) / seg;
          const dx = (b[0] - a[0]) / seg;
          const dz = (b[1] - a[1]) / seg;
          const off = p.width / 2 + 2;
          const x = a[0] + (b[0] - a[0]) * t + dz * off;
          const z = a[1] + (b[1] - a[1]) * t - dx * off;
          // Le panneau fait face aux skieurs qui descendent.
          push('sign_piste', x, hf.height(x, z), z, Math.atan2(-dx, -dz), 1, tint);
          next += 240;
        }
        acc += seg;
      }
    }
    // Jalons de piste, teintés selon la difficulté.
    const poleTint: Record<string, [number, number, number]> = {};
    const poles = placePistePoles(hf, pistes, lifts);
    for (const p of poles) push(p.name, p.x, p.y, p.z, p.yaw, p.s, (poleTint[p.difficulty] ??= linear(DIFFICULTY_TINT[p.difficulty] ?? 0xffffff)));
    this.houseCount = houses.length;
    this.poleCount = poles.length;
    this.treeCount = (lists.tree_fir_a?.length ?? 0) + (lists.tree_fir_b?.length ?? 0);
    this.rockCount = (lists.rock_a?.length ?? 0) + (lists.rock_b?.length ?? 0) + (lists.rock_c?.length ?? 0);
    this.batches = Object.entries(lists).map(([name, items]) => this.pack(name, items));
  }

  /**
   * Forêt et rochers du relief nu, tirés une seule fois par carte (sur une carte
   * de 4 km, plus de 300 000 points d'essai). Les tirages se font dans le même
   * ordre qu'au premier jour, les placements restent donc identiques.
   * Arbres et rochers : [x, z, cap, échelle, variante] à la suite.
   */
  private naturalDecor(): { trees: Float64Array; rocks: Float64Array } {
    if (this.natural) return this.natural;
    const hf = this.hf;
    const map = this.map;
    const rng = new Rng(map.features.seed * 31 + 7);
    const forest = createNoise2D(map.features.seed + 5);
    const v = map.features.village;
    const half = hf.half - 24;
    const trees: number[] = [];
    const rocks: number[] = [];
    // Forêt.
    const step = 8.5;
    for (let gz = -half; gz < half; gz += step) {
      for (let gx = -half; gx < half; gx += step) {
        const x = gx + rng.range(-3.6, 3.6);
        const z = gz + rng.range(-3.6, 3.6);
        const r = rng.next();
        const variant = rng.next();
        const sc = rng.range(0.72, 1.3);
        const yaw = rng.range(0, Math.PI * 2);
        const h = hf.height(x, z);
        const fn = fbm(forest, x / 420, z / 420, 3);
        const line = map.treeLine + fn * 45;
        if (h > line) continue;
        const slope = hf.slope(x, z);
        if (slope > 1.05) continue;
        let density = smoothstep(-0.2, 0.3, fn) * smoothstep(line, line - 70, h);
        if (h < 30) density *= 0.3;
        const dv = Math.hypot(x - v.x, z - v.z);
        if (dv < v.radius * 0.85) continue;
        if (dv < v.radius * 1.6) density *= 0.45;
        if (r > density) continue;
        const alt = 1 - Math.max(0, (h - (line - 120)) / 240);
        trees.push(x, z, yaw, sc * (0.75 + 0.25 * alt), variant < 0.55 ? 0 : 1);
      }
    }
    // Rochers : pentes fortes et haute altitude.
    for (let gz = -half; gz < half; gz += 13) {
      for (let gx = -half; gx < half; gx += 13) {
        const x = gx + rng.range(-5, 5);
        const z = gz + rng.range(-5, 5);
        const r = rng.next();
        const kind = rng.next();
        const sc = rng.range(0.8, 2.6);
        const yaw = rng.range(0, Math.PI * 2);
        const h = hf.height(x, z);
        const slope = hf.slope(x, z);
        const p = slope > 0.7 ? 0.26 : h > map.treeLine + 30 ? 0.07 : 0.012;
        if (r > p) continue;
        rocks.push(x, z, yaw, sc, kind < 0.45 ? 0 : kind < 0.8 ? 1 : 2);
      }
    }
    this.natural = { trees: Float64Array.from(trees), rocks: Float64Array.from(rocks) };
    return this.natural;
  }

  private pack(name: string, items: { x: number; y: number; z: number; yaw: number; s: number; tint: [number, number, number] }[]): InstanceBatch {
    const set = this.sets[name] as InstancedLodSet;
    const radius = this.lib.get(name).bounds.radius;
    const side = this.side;
    const size = (this.hf.half * 2) / side;
    const chunkOf = (x: number, z: number) => {
      const i = Math.min(side - 1, Math.max(0, Math.floor((x + this.hf.half) / size)));
      const j = Math.min(side - 1, Math.max(0, Math.floor((z + this.hf.half) / size)));
      return j * side + i;
    };
    items.sort((p, q) => chunkOf(p.x, p.z) - chunkOf(q.x, q.z));
    const n = items.length;
    const b: InstanceBatch = {
      set,
      m: new Float32Array(n * 16),
      s: new Float32Array(n * 4),
      tint: new Float32Array(n * 3),
      chunk: new Uint16Array(n),
      count: n,
      ranges: new Uint32Array(side * side * 2),
    };
    b.ranges.fill(0);
    let last = -1;
    items.forEach((it, k) => {
      composeYawRoll(b.m, k * 16, it.x, it.y, it.z, it.yaw, 0, it.s, it.s, it.s);
      b.s[k * 4] = it.x;
      b.s[k * 4 + 1] = it.y + radius * it.s * 0.5;
      b.s[k * 4 + 2] = it.z;
      b.s[k * 4 + 3] = radius * it.s;
      b.tint.set(it.tint, k * 3);
      const c = chunkOf(it.x, it.z);
      b.chunk[k] = c;
      if (c !== last) {
        b.ranges[c * 2] = k;
        last = c;
      }
      b.ranges[c * 2 + 1] = k + 1;
    });
    return b;
  }

  /** Culling et LOD (à appeler quand la caméra a bougé). */
  update(camera: THREE.Camera, frustum: Frustum, terrain: Terrain): void {
    const cp = camera.position;
    for (const s of Object.values(this.sets)) s.begin();
    for (const b of this.batches) {
      for (let c = 0; c < this.side * this.side; c++) {
        const start = b.ranges[c * 2] as number;
        const end = b.ranges[c * 2 + 1] as number;
        if (end <= start) continue;
        if (!(terrain.chunks[c] as { visible: boolean }).visible) continue;
        for (let k = start; k < end; k++) {
          const x = b.s[k * 4] as number;
          const y = b.s[k * 4 + 1] as number;
          const z = b.s[k * 4 + 2] as number;
          const r = b.s[k * 4 + 3] as number;
          if (!frustum.sphere(x, y, z, r)) continue;
          const d = Math.hypot(x - cp.x, y - cp.y, z - cp.z);
          b.set.add(b.m, k * 16, d, b.tint[k * 3] as number, b.tint[k * 3 + 1] as number, b.tint[k * 3 + 2] as number);
        }
      }
    }
    for (const s of Object.values(this.sets)) s.end();
  }

  stats(): { instances: number; triangles: number } {
    let instances = 0;
    let triangles = 0;
    for (const s of Object.values(this.sets)) {
      instances += s.instanceCount;
      triangles += s.triangleCount;
    }
    return { instances, triangles };
  }
}
